import * as React from "react";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import Layout from "../components/Layout";
import EvenGrid from "../components/EvenGrid";
import {
  arupLogo,
  bbcLogo,
  berylLogo,
  bromptonLogo,
  clippdLogo,
  geLogo,
  helLogo,
  innocentLogo,
  mottmacLogo,
  phillipsLogo,
  unileverLogo,
  weworkLogo,
} from "../media/images/logos";
import Seo from "../components/Seo";
import { Typography, Divider, useTheme } from "@mui/material";
import { Box, Stack } from "@mui/system";
import ProjectTile from "../components/ProjectTile";
import { Link } from "gatsby";
import TextSectionWrapper from "../components/TextSectionWrapper";
import ServicesTextSection from "../components/ServicesTextSection";
import { Service } from "../types";
import Grid from "@mui/system/Unstable_Grid";

const servicesArray: Service["servicesWithindustry"] = [
  {
    industry: "Design",
    services: [
      "UI / UX design",
      "Visual identity",
      "User research",
      "Design system",
      "Industrial design",
    ],
  },
  {
    industry: "Development",
    services: [
      "iOS & Android development",
      "Web development",
      "Cloud architecture",
      "Full-stack development",
      "Embedded software",
    ],
  },
  {
    industry: "Product leadership",
    services: [
      "Digital transformation",
      "Agile coaching",
      "Product strategy",
      "Investment support",
    ],
  },
];

const IndexPage = () => {
  const { customSpacing } = useTheme();

  return (
    <Stack
      divider={
        <Divider
          sx={{
            mt: customSpacing?.marginBottomLarge,
            mb: customSpacing?.marginTopLarge,
          }}
        />
      }
    >
      <Box>
        <Typography
          variant="body1"
          color="text.secondary"
          sx={{ display: "block", mt: customSpacing?.marginTopMedium }}
        >
          Making things since 2014
        </Typography>
        <Typography
          variant="h1"
          sx={{
            maxWidth: customSpacing?.titleMaxWidth,
            mt: { xs: 12, md: 15 },
          }}
        >
          Great ideas <br /> deserve to be made
        </Typography>
        <Typography
          component="p"
          variant="largeBody"
          maxWidth={customSpacing?.titleMaxWidth}
          sx={{
            mt: { xs: 2, sm: 3, md: 4 },
            mb: 8,
          }}
        >
          We bring award-winning design coupled with expert technical delivery
          to turn great ideas into exceptional products
        </Typography>
      </Box>
      <Stack>
        <Stack
          direction="row"
          justifyContent="space-between"
          mb={{
            xs: 2,
            sm: 4,
          }}
        >
          <Typography variant="body1">Selected projects</Typography>
          <Link
            to="/projects"
            style={{
              display: "flex",
              alignItems: "center",
            }}
          >
            <Typography variant="body1" color="secondary">
              See all
            </Typography>
            <ArrowForwardIcon
              sx={{ marginLeft: "5px", fontSize: 16 }}
              color="secondary"
            />
          </Link>
        </Stack>
        <Grid
          container
          direction="row"
          columnSpacing={customSpacing?.gutter}
          rowSpacing={{ xs: 6, sm: 8 }}
          alignItems="baseline"
          sx={{
            flexDirection: {
              xs: "column",
              sm: "row",
            },
          }}
        >
          <Grid xs={12} sm={6}>
            <Grid
              alignSelf="flex-start"
              id="drawing-top-left-tile"
              px={0}
              pt={5}
              pb={4}
            >
              <Grid px={0} py={4}>
                <ProjectTile shouldHaveAnimationOnHover projectSlug="thyia" />
              </Grid>
              <ProjectTile
                shouldHaveAnimationOnHover
                projectSlug="carbon-portal"
              />
            </Grid>
            <Grid px={0} py={4} id="drawing-mid-left-tile">
              <ProjectTile shouldHaveAnimationOnHover projectSlug="courtyard" />
            </Grid>
            <Grid px={0} id="drawing-bottom-left-tile" py={4}>
              <ProjectTile
                shouldHaveAnimationOnHover
                projectSlug="london-centric"
              />
            </Grid>
          </Grid>
          <Grid
            pt={{ xs: 0 }}
            xs={12}
            sm={6}
            mt={{ xs: 0, sm: 19, md: 26 }}
            alignSelf="flex-end"
          >
            <Grid px={0} id="drawing-top-right-tile" py={4}>
              <ProjectTile shouldHaveAnimationOnHover projectSlug="phillips" />
            </Grid>
            <Grid px={0} id="drawing-mid-right-tile" py={4}>
              <ProjectTile shouldHaveAnimationOnHover projectSlug="fittle" />
            </Grid>
            <Grid px={0} id="drawing-mid-right-tile" py={4}>
              <ProjectTile shouldHaveAnimationOnHover projectSlug="plexaa" />
            </Grid>
            <Grid
              alignSelf="flex-start"
              px={0}
              id="drawing-bottom-right-tile"
              pt={4}
              pb={0}
            >
              <ProjectTile
                shouldHaveAnimationOnHover
                projectSlug="irida-health"
              />
            </Grid>
          </Grid>
        </Grid>
      </Stack>

      <Stack
        width="100%"
        sx={{
          display: "flex",
          justifyContent: "space-between",
          flexDirection: { sm: "column", md: "row" },
        }}
        gap={customSpacing?.gutter}
      >
        <Box flex={1}>
          <Typography variant="subtitle1">Our clients</Typography>
          <Typography
            variant="body1"
            color="text.secondary"
            maxWidth={customSpacing?.bodyTexBlockMaxWidth}
            mt={1.5}
          >
            We have had the privilege of working with some of the worlds biggest
            brands as well as a fantastic collection of start ups and
            entrepreneurs.
          </Typography>
        </Box>
        <Box flex={1}>
          <EvenGrid
            justifyContent="space-between"
            sx={{ width: "100%" }}
            itemColumns={{ xs: 4, sm: 3, md: 3 }}
            columnSpacing={{ xs: 0, sm: 3.625 }}
          >
            <img src={innocentLogo} alt="Innocent Drinks" />
            <img src={unileverLogo} alt="Unilever" />
            <img src={arupLogo} alt="Arup" />
            <img src={mottmacLogo} alt="Mott MacDonald" />
            <img src={clippdLogo} alt="Clippd" />
            <img src={weworkLogo} alt="WeWork" />
            <img src={bromptonLogo} alt="Brompton Bikes" />
            <img src={helLogo} alt="H.E.L" />
            <img src={bbcLogo} alt="BBC" />
            <img src={berylLogo} alt="Beryl" />
            <img src={geLogo} alt="GE" />
            <img src={phillipsLogo} alt="Phillips" />
          </EvenGrid>
        </Box>
      </Stack>
      <ServicesTextSection
        service={{
          title: "Services",
          description:
            "From concept to delivery, we are a product swat team of multidisciplinary designers and engineers ready to bring your ideas to life. In no particular order we do:",
          servicesWithindustry: servicesArray,
        }}
      />
      <Box>
        <TextSectionWrapper>
          <Box flex={1}>
            <Typography variant="subtitle1">Our values</Typography>
            <Typography
              variant="body1"
              maxWidth={customSpacing?.bodyTexBlockMaxWidth}
              mt={1.5}
              color="text.secondary"
            >
              We’re a company founded by friends with a love of making, how we
              work is just as as important to us as the work that we do.
            </Typography>
          </Box>
        </TextSectionWrapper>
        <TextSectionWrapper
          mt={{ xs: 6, sm: 8, md: 12 }}
          direction={{ xs: "column", sm: "row" }}
          sx={{
            "& ul": {
              paddingLeft: 0,
              mb: 0,
              mt: 1.5,
              listStyle: "none",
            },
          }}
          gap={{}}
        >
          <Box flex={1}>
            <Typography variant="subtitle2">Caring</Typography>
            <Typography
              variant="body1"
              component="ul"
              maxWidth="600px"
              color="text.secondary"
            >
              <li>A Studio built on friendship </li>
              <li>Flexibility built into the way we work</li>
              <li>Benefits shaped by employees</li>
            </Typography>
          </Box>
          <Box flex={1} mt={{ xs: 4, sm: 0 }}>
            <Typography variant="subtitle2">Considered</Typography>
            <Typography
              variant="body1"
              component="ul"
              maxWidth="600px"
              color="text.secondary"
            >
              <li>Pragmatic at every stage </li>
              <li>Bringing order to chaos</li>
              <li>Embracing complex challenges</li>
            </Typography>
          </Box>
        </TextSectionWrapper>
        <TextSectionWrapper
          direction={{ xs: "column", sm: "row" }}
          my={{ xs: 4, sm: 6, md: 8 }}
          sx={{
            "& ul": {
              paddingLeft: 0,
              mb: 0,
              mt: 1.5,
              listStyle: "none",
            },
          }}
          gap={{}}
        >
          <Box flex={1}>
            <Typography variant="subtitle2">Collaborative</Typography>
            <Typography
              variant="body1"
              component="ul"
              maxWidth="600px"
              color="text.secondary"
            >
              <li>Design & Development working in unison </li>
              <li>One team mentality - our clients are partners </li>
              <li>Agile working bringing us together </li>
            </Typography>
          </Box>
          <Box flex={1} mt={{ xs: customSpacing?.verticalSpacing?.xs, sm: 0 }}>
            <Typography variant="subtitle2">Creative</Typography>
            <Typography
              variant="body1"
              component="ul"
              maxWidth="600px"
              color="text.secondary"
            >
              <li>Passionate about making</li>
              <li>We embrace make-test-learn</li>
              <li>Creatively ambitious</li>
            </Typography>
          </Box>
        </TextSectionWrapper>
        <Stack alignItems="flex-end">
          <Link
            to="/team"
            style={{
              display: "inline-flex",
              alignItems: "center",
            }}
          >
            <Typography variant="body1" color="secondary.main">
              Get to know us
            </Typography>
            <ArrowForwardIcon
              sx={{ marginLeft: "5px", fontSize: "24px" }}
              color="secondary"
            />
          </Link>
        </Stack>
      </Box>
    </Stack>
  );
};

export const Head = () => (
  <Seo
    title="Home"
    description="We are Mettle. We build products that help organisations realise their potential to create positive change."
  />
);

const IndexPageLayoutWrapper = () => (
  <Layout>
    <IndexPage />
  </Layout>
);

export default IndexPageLayoutWrapper;
